import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  CheckCircleOutline,
  Delete,
  Edit,
  ExpandLess,
  ExpandMore,
  FactCheck,
  PendingActions,
  WarningAmber,
} from "@mui/icons-material";
import {
  useGetSheqInspectionsQuery,
  useCreateSheqInspectionMutation,
  useUpdateSheqInspectionMutation,
  useDeleteSheqInspectionMutation,
  SheqInspection,
  InspectionType,
  InspectionOutcome,
} from "../../../redux/sheqApi";
import { useGetComsBlocksQuery } from "../../../redux/comsApi";
import { useGetLabourWorkersQuery } from "../../../redux/labourTrackerApi";

const NAVY = "#123456";

const INSPECTION_TYPES: { value: InspectionType; label: string }[] = [
  { value: "TOOLBOX_TALK", label: "Toolbox Talk" },
  { value: "SITE_INSPECTION", label: "Site Inspection" },
  { value: "PPE_CHECK", label: "PPE Check" },
  { value: "HOUSEKEEPING", label: "Housekeeping" },
  { value: "FIRE_EQUIPMENT", label: "Fire Equipment" },
  { value: "ELECTRICAL", label: "Electrical" },
  { value: "SCAFFOLD", label: "Scaffold / Falsework" },
  { value: "LIFTING", label: "Lifting Equipment" },
  { value: "GENERAL", label: "General" },
];

const OUTCOMES: InspectionOutcome[] = ["PASS", "FAIL", "CONDITIONAL"];

const OUTCOME_COLOR: Record<InspectionOutcome, string> = {
  PASS: "#2e7d32",
  FAIL: "#c62828",
  CONDITIONAL: "#f57c00",
};

function today() {
  return new Date().toISOString().slice(0, 10);
}

function daysFromNow(dateStr?: string): number {
  if (!dateStr) return 9999;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const d = new Date(`${dateStr}T00:00:00`);
  return Math.round((d.getTime() - now.getTime()) / 86400000);
}

type WorkQueueUrgency = "overdue" | "today" | "week" | "upcoming";

function getUrgency(daysAway: number): WorkQueueUrgency {
  if (daysAway < 0) return "overdue";
  if (daysAway === 0) return "today";
  if (daysAway <= 7) return "week";
  return "upcoming";
}

const URGENCY_COLOR: Record<
  WorkQueueUrgency,
  { bg: string; border: string; text: string; label: string }
> = {
  overdue: {
    bg: "#fff8f8",
    border: "#c62828",
    text: "#c62828",
    label: "Overdue",
  },
  today: {
    bg: "#fff3e0",
    border: "#e65100",
    text: "#e65100",
    label: "Due today",
  },
  week: {
    bg: "#fffde7",
    border: "#f9a825",
    text: "#f57c00",
    label: "Due this week",
  },
  upcoming: {
    bg: "#f1f8e9",
    border: "#558b2f",
    text: "#2e7d32",
    label: "Upcoming",
  },
};

// ─── Work Queue panel ───────────────────────────────────────────────────────
interface WorkQueueProps {
  inspections: SheqInspection[];
  onEdit: (insp: SheqInspection) => void;
  onResolve: (insp: SheqInspection) => void;
}

const WorkQueuePanel: React.FC<WorkQueueProps> = ({
  inspections,
  onEdit,
  onResolve,
}) => {
  const [open, setOpen] = useState(true);

  const queue = useMemo(() => {
    return inspections
      .filter((i) => i.followUpStatus === "OPEN" && i.followUpDueDate)
      .map((i) => ({
        insp: i,
        days: daysFromNow(i.followUpDueDate),
        urgency: getUrgency(daysFromNow(i.followUpDueDate)),
      }))
      .sort((a, b) => a.days - b.days);
  }, [inspections]);

  if (queue.length === 0) return null;

  const overdue = queue.filter((q) => q.urgency === "overdue").length;
  const dueToday = queue.filter((q) => q.urgency === "today").length;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 3,
        borderColor:
          overdue > 0 ? "#c62828" : dueToday > 0 ? "#e65100" : "#f9a825",
        borderWidth: 2,
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1.5}
        sx={{
          bgcolor:
            overdue > 0 ? "#fff8f8" : dueToday > 0 ? "#fff3e0" : "#fffde7",
          borderBottom: open ? "1px solid #e0e0e0" : "none",
          cursor: "pointer",
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <PendingActions
            sx={{ color: overdue > 0 ? "#c62828" : "#f57c00", fontSize: 22 }}
          />
          <Typography variant="subtitle1" fontWeight={700}>
            Inspection Work Queue
          </Typography>
          <Badge
            badgeContent={queue.length}
            color={overdue > 0 ? "error" : "warning"}
          />
          {overdue > 0 && (
            <Chip
              label={`${overdue} overdue`}
              size="small"
              icon={<WarningAmber sx={{ fontSize: "14px !important" }} />}
              sx={{
                bgcolor: "#c62828",
                color: "#fff",
                fontSize: 11,
                height: 22,
              }}
            />
          )}
          {dueToday > 0 && (
            <Chip
              label={`${dueToday} due today`}
              size="small"
              sx={{
                bgcolor: "#e65100",
                color: "#fff",
                fontSize: 11,
                height: 22,
              }}
            />
          )}
        </Box>
        <IconButton size="small">
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      {/* Queue items */}
      <Collapse in={open}>
        <Box px={2} py={1} display="flex" flexDirection="column" gap={1}>
          {queue.map(({ insp, days, urgency }) => {
            const { bg, border, text, label } = URGENCY_COLOR[urgency];
            const typeLabel =
              INSPECTION_TYPES.find((t) => t.value === insp.type)?.label ??
              insp.type;
            return (
              <Box
                key={insp.id}
                sx={{
                  bgcolor: bg,
                  border: `1px solid ${border}`,
                  borderRadius: 1.5,
                  px: 2,
                  py: 1.25,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                {/* Urgency badge */}
                <Chip
                  label={label}
                  size="small"
                  sx={{
                    bgcolor: border,
                    color: "#fff",
                    fontSize: 10,
                    height: 20,
                    mr: 0.5,
                  }}
                />
                {/* Info */}
                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {insp.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {typeLabel}
                    {insp.blockName ? ` · ${insp.blockName}` : ""}
                    {insp.conductedByName ? ` · ${insp.conductedByName}` : ""}
                  </Typography>
                </Box>
                {/* Due label */}
                <Typography
                  variant="caption"
                  sx={{ color: text, fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {urgency === "overdue"
                    ? `${Math.abs(days)}d overdue`
                    : urgency === "today"
                    ? "Today"
                    : `in ${days}d · ${insp.followUpDueDate}`}
                </Typography>
                {/* Follow-up actions preview */}
                {insp.followUpActions && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      width: "100%",
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      mt: 0.25,
                    }}
                  >
                    Action: {insp.followUpActions}
                  </Typography>
                )}
                {/* Actions */}
                <Stack direction="row" gap={0.5} ml="auto">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => onEdit(insp)}>
                      <Edit sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Mark follow-up resolved">
                    <IconButton
                      size="small"
                      sx={{ color: "#2e7d32" }}
                      onClick={() => onResolve(insp)}
                    >
                      <CheckCircleOutline sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Card>
  );
};

// ─── Inspection dialog ──────────────────────────────────────────────────────
interface InspectionDialogProps {
  open: boolean;
  inspection: SheqInspection | null;
  onClose: () => void;
}

const emptyInspection = (): Partial<SheqInspection> => ({
  title: "",
  inspectionDate: today(),
  type: "SITE_INSPECTION",
  outcome: "PASS",
  attendeeCount: 0,
  followUpStatus: "OPEN",
});

const InspectionDialog: React.FC<InspectionDialogProps> = ({
  open,
  inspection,
  onClose,
}) => {
  const { data: blocks = [] } = useGetComsBlocksQuery();
  const { data: workers = [] } = useGetLabourWorkersQuery({ active: true });
  const [create, { isLoading: creating }] = useCreateSheqInspectionMutation();
  const [update, { isLoading: updating }] = useUpdateSheqInspectionMutation();

  const [form, setForm] = useState<Partial<SheqInspection>>(
    inspection ?? emptyInspection(),
  );

  React.useEffect(() => {
    setForm(inspection ?? emptyInspection());
  }, [inspection, open]);

  const set = (key: keyof SheqInspection, val: unknown) =>
    setForm((f) => ({ ...f, [key]: val }));

  const editing = !!inspection;
  const saving = creating || updating;

  const handleSave = async () => {
    if (!form.title || !form.inspectionDate || !form.type) return;
    const block = blocks.find((b) => b.id === form.blockId);
    const payload = {
      ...form,
      blockName: block?.name ?? form.blockName,
      attendeeCount: form.attendeeCount ?? 0,
      followUpStatus: form.followUpStatus ?? "OPEN",
    } as Omit<SheqInspection, "id" | "createdAt" | "updatedAt">;
    try {
      if (editing && inspection?.id) {
        await update({ id: inspection.id, body: payload }).unwrap();
      } else {
        await create(payload).unwrap();
      }
      onClose();
    } catch {
      // handle
    }
  };

  const isToolbox = form.type === "TOOLBOX_TALK";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editing
          ? "Edit Inspection"
          : isToolbox
          ? "Record Toolbox Talk"
          : "Record Inspection"}
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label="Title"
            size="small"
            required
            value={form.title ?? ""}
            onChange={(e) => set("title", e.target.value)}
          />

          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Date"
              type="date"
              size="small"
              required
              value={form.inspectionDate ?? ""}
              onChange={(e) => set("inspectionDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: "1 1 140px" }}
            />
            <FormControl size="small" sx={{ flex: "1 1 180px" }}>
              <InputLabel>Site / Block</InputLabel>
              <Select
                label="Site / Block"
                value={form.blockId ?? ""}
                onChange={(e) => set("blockId", e.target.value)}
              >
                <MenuItem value="">— None —</MenuItem>
                {blocks.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: "1 1 180px" }} required>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={form.type ?? "SITE_INSPECTION"}
                onChange={(e) => set("type", e.target.value)}
              >
                {INSPECTION_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: "1 1 140px" }}>
              <InputLabel>Outcome</InputLabel>
              <Select
                label="Outcome"
                value={form.outcome ?? "PASS"}
                onChange={(e) => set("outcome", e.target.value)}
              >
                {OUTCOMES.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Conducted by (select from workers) */}
          <FormControl fullWidth size="small">
            <InputLabel>Conducted by</InputLabel>
            <Select
              label="Conducted by"
              value={form.conductedBy ?? ""}
              onChange={(e) => {
                const w = workers.find((x) => x.id === e.target.value);
                set("conductedBy", e.target.value);
                set("conductedByName", w?.name ?? "");
              }}
            >
              <MenuItem value="">— Not specified —</MenuItem>
              {workers
                .filter((w) => w.role === "Supervisor" || w.role === "Artisan")
                .map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name} ({w.role})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {isToolbox ? (
            <>
              <TextField
                label="Topics Covered"
                size="small"
                multiline
                rows={2}
                value={form.topicsCovered ?? ""}
                onChange={(e) => set("topicsCovered", e.target.value)}
                placeholder="e.g. Working at height, PPE compliance, housekeeping..."
              />
              <TextField
                label="Attendees (names)"
                size="small"
                multiline
                rows={3}
                value={form.attendees ?? ""}
                onChange={(e) => set("attendees", e.target.value)}
                placeholder="One name per line or comma-separated"
              />
              <TextField
                label="Attendee count"
                type="number"
                size="small"
                value={form.attendeeCount ?? 0}
                onChange={(e) => set("attendeeCount", Number(e.target.value))}
                inputProps={{ min: 0 }}
                sx={{ width: 150 }}
              />
            </>
          ) : (
            <>
              <TextField
                label="Findings"
                size="small"
                multiline
                rows={3}
                value={form.findings ?? ""}
                onChange={(e) => set("findings", e.target.value)}
              />
              <TextField
                label="Recommendations"
                size="small"
                multiline
                rows={2}
                value={form.recommendations ?? ""}
                onChange={(e) => set("recommendations", e.target.value)}
              />
            </>
          )}

          {/* Follow-up */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <FormControl size="small" sx={{ flex: "1 1 160px" }}>
              <InputLabel>Follow-up status</InputLabel>
              <Select
                label="Follow-up status"
                value={form.followUpStatus ?? "OPEN"}
                onChange={(e) => set("followUpStatus", e.target.value)}
              >
                <MenuItem value="OPEN">Open</MenuItem>
                <MenuItem value="CLOSED">Closed</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Follow-up due date"
              type="date"
              size="small"
              value={form.followUpDueDate ?? ""}
              onChange={(e) => set("followUpDueDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: "1 1 150px" }}
            />
          </Box>

          {form.followUpStatus === "OPEN" && (
            <TextField
              label="Follow-up actions required"
              size="small"
              multiline
              rows={2}
              value={form.followUpActions ?? ""}
              onChange={(e) => set("followUpActions", e.target.value)}
            />
          )}

          <TextField
            label="Notes"
            size="small"
            multiline
            rows={2}
            value={form.notes ?? ""}
            onChange={(e) => set("notes", e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.title}
          sx={{ bgcolor: NAVY }}
        >
          {saving ? <CircularProgress size={16} /> : editing ? "Save" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────
interface Props {
  blockId?: string;
}

export const SheqInspections: React.FC<Props> = ({ blockId }) => {
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [outcomeFilter, setOutcomeFilter] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editInspection, setEditInspection] = useState<SheqInspection | null>(
    null,
  );

  const { data: inspections = [], isLoading } = useGetSheqInspectionsQuery({
    blockId: blockId || undefined,
    type: typeFilter || undefined,
    outcome: outcomeFilter || undefined,
  });
  const [deleteInspection] = useDeleteSheqInspectionMutation();
  const [updateInspection] = useUpdateSheqInspectionMutation();

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this record?")) return;
    await deleteInspection(id);
  };

  const handleResolveFollowUp = async (insp: SheqInspection) => {
    if (!insp.id) return;
    if (!window.confirm(`Mark follow-up resolved for "${insp.title}"?`)) return;
    await updateInspection({ id: insp.id, body: { followUpStatus: "CLOSED" } });
  };

  const openEdit = (insp: SheqInspection) => {
    setEditInspection(insp);
    setDialogOpen(true);
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" fontWeight={700}>
          Inspections &amp; Toolbox Talks
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditInspection(null);
            setDialogOpen(true);
          }}
          sx={{ bgcolor: NAVY }}
        >
          Add Record
        </Button>
      </Box>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Type</InputLabel>
          <Select
            label="Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="">All types</MenuItem>
            {INSPECTION_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Outcome</InputLabel>
          <Select
            label="Outcome"
            value={outcomeFilter}
            onChange={(e) => setOutcomeFilter(e.target.value)}
          >
            <MenuItem value="">All outcomes</MenuItem>
            {OUTCOMES.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* ── Work Queue ─────────────────────────────────────────────── */}
          <WorkQueuePanel
            inspections={inspections}
            onEdit={openEdit}
            onResolve={handleResolveFollowUp}
          />

          {/* ── Records list ───────────────────────────────────────────── */}
          {inspections.length === 0 ? (
            <Box textAlign="center" py={8}>
              <FactCheck sx={{ fontSize: 56, color: "#ccc", mb: 2 }} />
              <Typography color="text.secondary">
                No inspection records found.
              </Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {inspections.map((insp) => {
                const typeLabel =
                  INSPECTION_TYPES.find((t) => t.value === insp.type)?.label ??
                  insp.type;
                return (
                  <Card key={insp.id} sx={{ p: 2 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      flexWrap="wrap"
                      gap={1}
                    >
                      <Box flex={1} minWidth={0}>
                        <Box
                          display="flex"
                          gap={1}
                          alignItems="center"
                          mb={0.5}
                          flexWrap="wrap"
                        >
                          <Typography variant="body1" fontWeight={700}>
                            {insp.title}
                          </Typography>
                          <Chip
                            label={typeLabel}
                            size="small"
                            sx={{ fontSize: 11 }}
                          />
                          <Chip
                            label={insp.outcome}
                            size="small"
                            sx={{
                              bgcolor:
                                OUTCOME_COLOR[
                                  insp.outcome as InspectionOutcome
                                ] ?? "#555",
                              color: "#fff",
                              fontSize: 11,
                            }}
                          />
                          {insp.followUpStatus === "OPEN" &&
                            insp.type !== "TOOLBOX_TALK" && (
                              <Chip
                                label="Follow-up open"
                                size="small"
                                color="warning"
                                sx={{ fontSize: 11 }}
                              />
                            )}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mb={0.5}
                        >
                          {insp.inspectionDate}
                          {insp.blockName ? ` · ${insp.blockName}` : ""}
                          {insp.conductedByName
                            ? ` · ${insp.conductedByName}`
                            : ""}
                          {insp.type === "TOOLBOX_TALK" && insp.attendeeCount
                            ? ` · ${insp.attendeeCount} attendees`
                            : ""}
                        </Typography>
                        {insp.type === "TOOLBOX_TALK" && insp.topicsCovered && (
                          <Typography variant="body2" color="text.secondary">
                            Topics: {insp.topicsCovered}
                          </Typography>
                        )}
                        {insp.findings && (
                          <Typography
                            variant="body2"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {insp.findings}
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openEdit(insp)}
                          >
                            <Edit sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(insp.id!)}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Card>
                );
              })}
            </Box>
          )}
        </>
      )}

      <InspectionDialog
        open={dialogOpen}
        inspection={editInspection}
        onClose={() => {
          setDialogOpen(false);
          setEditInspection(null);
        }}
      />
    </Box>
  );
};
